import { useContext } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { DiscoveryContext } from "../../luminaire/DiscoveryV2/discovery-context";
import { getBrandPillarsDetail, getBrandPillarsAvg } from "../../utils/brandIndexCalc";
import { ENTITY_LABELS } from "../../utils/labelMapping";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

function scoreColor(score) {
  if (score == null) return "#8a94a6";
  if (score >= 9) return "#198754";
  if (score >= 7) return "#2196F3";
  return "#eca336";
}

/**
 * Shows all brand pillar (core attribute) scores for a single brand/competitor.
 * Reads directly from DiscoveryContext.
 */
export default function BrandPillarsTable({ brandProduct, coreAttributesData }) {
  const discoveryCtx = useContext(DiscoveryContext);
  const source = coreAttributesData ?? discoveryCtx.core_attributes;
  const rows = getBrandPillarsDetail(brandProduct, source);
  const avg = getBrandPillarsAvg(brandProduct, source);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
        {ENTITY_LABELS.brandPillar} data not yet available.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ width: "100%", tableLayout: "fixed" }} size="medium">
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "60%" }} />
        </colgroup>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Attribute</StyledTableCell>
            <StyledTableCell align="center">Score (0–10)</StyledTableCell>
            <StyledTableCell>Explanation</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <StyledTableRow key={row.name}>
              <StyledTableCell component="th" scope="row">
                <Tooltip title={row.definition} placement="top" arrow>
                  <span style={{ cursor: "default", borderBottom: "1px dashed #8a94a6" }}>
                    {row.name}
                  </span>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align="center">
                <span style={{ color: scoreColor(row.score), fontWeight: 600 }}>
                  {row.score != null ? Number(row.score).toFixed(1) : "—"}
                </span>
              </StyledTableCell>
              <StyledTableCell sx={{ wordBreak: "break-word" }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                  {row.explanation || "—"}
                </Typography>
              </StyledTableCell>
            </StyledTableRow>
          ))}
          <TableRow sx={{ backgroundColor: "#f0f4f8" }}>
            <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>Average</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, color: scoreColor(avg) }}>
              {avg != null ? avg.toFixed(2) : "—"}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
